import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { matchesSearchFilters, type DatabaseLoad, type LoadSearchParams, type GeoCoords } from '@/lib/loadSearch';
import type { RealtimeChannel } from '@supabase/supabase-js';

/** How long a faded row stays visible before removal (ms) */
export const FADE_REMOVAL_DELAY_MS = 180_000;

interface UseLoadBoardRealtimeOptions {
  /** Whether the subscription should be active */
  enabled: boolean;
  /** The search params used for the active tab */
  searchParams: LoadSearchParams | null;
  /** Geocoded coordinates for origin (null if not geocoded or not needed) */
  originCoords: GeoCoords | null;
  /** Geocoded coordinates for destination */
  destCoords: GeoCoords | null;
  /** Set of load IDs currently visible in results (for UPDATE matching) */
  visibleLoadIds: Set<string>;
  /** Whether the current user is a demo user (for demo isolation) */
  isDemoUser: boolean;
  /** Callback when a new matching load should be added */
  onNewLoad: (load: DatabaseLoad) => void;
  /** Callback when a visible load should start fading (with its new status) */
  onFadeLoad: (loadId: string, newStatus: string) => void;
  /**
   * Optional self-heal callback. Fires when the realtime channel reaches
   * SUBSCRIBED (initial + reconnect), on window focus, and on tab-visible —
   * lets the parent refetch after a CHANNEL_ERROR gap so INSERT/UPDATE
   * events dropped during the gap don't leave results stale.
   */
  onResync?: () => void;
}

export function useLoadBoardRealtime({
  enabled,
  searchParams,
  originCoords,
  destCoords,
  visibleLoadIds,
  isDemoUser,
  onNewLoad,
  onFadeLoad,
  onResync,
}: UseLoadBoardRealtimeOptions) {
  // Keep refs for callbacks + visibleLoadIds so the channel handler
  // always sees the latest values without re-subscribing.
  const visibleRef = useRef(visibleLoadIds);
  visibleRef.current = visibleLoadIds;

  const onNewLoadRef = useRef(onNewLoad);
  onNewLoadRef.current = onNewLoad;

  const onFadeLoadRef = useRef(onFadeLoad);
  onFadeLoadRef.current = onFadeLoad;

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const originCoordsRef = useRef(originCoords);
  originCoordsRef.current = originCoords;

  const destCoordsRef = useRef(destCoords);
  destCoordsRef.current = destCoords;

  const onResyncRef = useRef(onResync);
  onResyncRef.current = onResync;

  useEffect(() => {
    if (!enabled || !searchParams) return;

    const channel: RealtimeChannel = supabase
      .channel('load-board-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'loads' },
        (payload) => {
          const newLoad = payload.new as DatabaseLoad & { is_demo?: boolean };

          // Demo isolation: only process events matching demo status
          if ((newLoad.is_demo ?? false) !== isDemoUser) return;

          // Forward all posted-load INSERTs to the page handler.
          // The page decides per-tab whether the load matches (active tab gets
          // toast/chime/prepend, inactive searched tabs get an unseen badge).
          onNewLoadRef.current(newLoad);
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'loads' },
        (payload) => {
          const updated = payload.new as DatabaseLoad & { is_demo?: boolean };

          // Demo isolation: only process events matching demo status
          if ((updated.is_demo ?? false) !== isDemoUser) return;

          // Only care about loads that are currently visible
          if (!visibleRef.current.has(updated.id)) return;

          // If status is no longer 'posted', trigger fade
          if (updated.status !== 'posted') {
            onFadeLoadRef.current(updated.id, updated.status);
          }
        },
      )
      .subscribe((status) => {
        // Self-heal on (re)subscribe — parent can refetch to backfill any
        // INSERT/UPDATE dropped during a CHANNEL_ERROR gap.
        if (status === 'SUBSCRIBED') onResyncRef.current?.();
      });

    // Focus / visibility self-heal — also nudges the parent to refetch.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; onResyncRef.current?.(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === 'visible') runDebounced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
    // Re-subscribe only when enabled toggles or the search identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, searchParams]);
}
